/**
 * Single source of truth for interpreting a boolean preference value.
 *
 * Preference values arrive from the backend as raw strings ("true"/"false",
 * but also "0"/"1" when written via SQL/scripts). A non-empty string like "0"
 * is TRUTHY in JavaScript, so `Boolean("0")` / `!!"0"` is `true` — while the
 * Python backend (`get_bool_preference`) correctly treats "0" as false. That
 * mismatch made a disabled setting (e.g. OPENSEARCH_ENABLED="0") render as
 * enabled in Settings (DRFS-626).
 *
 * Use this helper everywhere a preference is read as a boolean so the UI always
 * matches the backend's interpretation.
 */

const FALSY_PREFERENCE_STRINGS = new Set([
  '',
  '0',
  'false',
  'no',
  'off',
  'null',
  // Python str(None) == "None": a null written for a boolean pref can reach the
  // client as the string "None". Kept in lockstep with the backend
  // _FALSY_PREFERENCE_STRINGS (doc2-api helpers/preferences_util.py).
  'none',
  'undefined',
]);

export function toBoolPreference(value: unknown): boolean {
  if (typeof value === 'string') {
    return !FALSY_PREFERENCE_STRINGS.has(value.trim().toLowerCase());
  }
  return Boolean(value);
}
